import { ServiceTypeOffersPageStore } from './service-type-offers-page.store';
import { fb, routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { Component, computed, inject, viewChild } from '@angular/core';
import { getSocialServiceOperationsFrag, getSocialServiceRegionsFrag } from './service-type-offers-page.query';
import {
    boolColumn,
    dateField,
    descriptionColumn,
    nameColumn,
    textColumn,
} from '../../../../modules/shared/components/kit/table/table.fields';
import { FilterBuilder } from '../../../../modules/content/helper/filter.builder';
import {
    SocialServiceOperationObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-operation.object';
import {
    ServiceTypeOperationUpsertDialogComponent,
} from './service-type-operation-upsert-dialog/service-type-operation-upsert-dialog.component';
import {
    socialServiceOperationPaymentEnumOptions,
} from '@libs/types/src/services/enum/social-service-operation-payment.enum';
import { socialServiceOperationEnumOptions } from '@libs/types/src/services/enum/social-service-operation.enum';
import { TableComponent } from '../../../../modules/shared/components/kit/table/table.component';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import {
    SocialServiceRegionObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-region.object';
import {
    ServiceTypeRegionUpsertDialogComponent,
} from './service-type-region-upsert-dialog/service-type-region-upsert-dialog.component';
import { SwitchInputComponent } from '../../../kit/input/switch-input/switch-input.component';
import { changeSignal } from '../../../../utils/form.utils';
import { measurementUnitEnumOptions } from '@libs/types/src/common/enum/measurement-unit.enum';
import {
    socialServiceFacultativeCodeEnumOptions,
} from '@libs/types/src/services/enum/social-service-facultative-code.enum';
import {
    socialServiceInformativeCodeEnumOptions,
} from '@libs/types/src/services/enum/social-service-informative-code.enum';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import {
    ServiceTypeResidualPercentTableComponent,
} from './service-type-residual-percent-table/service-type-residual-percent-table.component';

@Component({
    selector: 'app-service-type-offers-page',
    templateUrl: './service-type-offers-page.component.html',
    styleUrl: './service-type-offers-page.component.scss',
    providers: [ServiceTypeOffersPageStore],
    imports: [
        SwitchInputComponent,
        TableComponent,
        ServiceTypeResidualPercentTableComponent,
    ],
})
export class ServiceTypeOffersPageComponent {

    public readonly store = inject(ServiceTypeOffersPageStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);

    protected readonly operationTable = viewChild<TableComponent>('operationTable');
    protected readonly regionTable = viewChild<TableComponent>('regionTable');

    protected form = fb.group({
        operationActiveOnly: fb.control(true),
        regionActiveOnly: fb.control(true),
    });
    protected operationPrefix = 'serviceType.operation.';
    protected regionPrefix = 'serviceType.region.';

    protected operationActiveChange = changeSignal(this.form.controls.operationActiveOnly);
    protected additionalFilterOperation = computed(() => {
        const builder = new FilterBuilder();
        builder.where('serviceTypeId', routeParams.serviceTypeId());
        if (this.operationActiveChange()) {
            builder.validByNow();
        }

        return builder.filter().filter;
    });

    protected regionActiveChange = changeSignal(this.form.controls.regionActiveOnly);
    protected additionalFilterRegion = computed(() => {
        const builder = new FilterBuilder();
        builder.where('serviceTypeId', routeParams.serviceTypeId());
        if (this.regionActiveChange()) {
            builder.validByNow();
        }

        return builder.filter().filter;
    });

    protected readonly getSocialServiceOperationsFrag = getSocialServiceOperationsFrag;

    protected operationColumns = [
        nameColumn,
        {
            ...textColumn('type'),
            filterable: {
                type: 'select',
                selectOptions: () => socialServiceOperationEnumOptions,
            },
            data: (item: any) => translate.instant(`${this.operationPrefix}type.${item}`),
        },
        {
            ...textColumn('billCode'),
            filterable: {
                type: 'select',
                selectOptions: () => {
                    return ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'p', 'y', 'z'].map((it) => {
                        return {
                            name: it,
                            value: it,
                        };
                    });
                },
            },
        },
        boolColumn('mpsvBasic'),
        boolColumn('mpsvMeeting'),
        boolColumn('noRecord'),
        {
            ...textColumn('facultativeCode'),
            filterable: {
                type: 'select',
                selectOptions: () => socialServiceFacultativeCodeEnumOptions,
            },
            data: (item: any) => item ? translate.instant(`serviceType.operation.facultativeCode.${item}`) : '',
        },
        {
            ...textColumn('informationCode'),
            filterable: {
                type: 'select',
                selectOptions: () => socialServiceInformativeCodeEnumOptions,
            },
            data: (item: any) => item ? translate.instant(`serviceType.operation.informationCode.${item}`) : '',
        },
        {
            ...textColumn('unit'),
            filterable: {
                type: 'select',
                selectOptions: () => measurementUnitEnumOptions,
            },
            data: (item: any) => item ? translate.instant(`enum.measurementUnit.${item}`) : '',
        },
        {
            ...textColumn('payment'),
            filterable: {
                type: 'select',
                selectOptions: () => socialServiceOperationPaymentEnumOptions,
            },
            data: (item: any) => item ? translate.instant(`${this.operationPrefix}payment.${item}`) : '',
        },
        textColumn('maxPrice'),
        textColumn('maxPriceExt'),
        dateField('validFrom'),
        dateField('validTo'),
    ];

    protected regionColumns = [
        nameColumn,
        descriptionColumn,
        dateField('validFrom'),
        dateField('validTo'),
    ];

    protected openOperationDetail(item: SocialServiceOperationObject | null = null): void {
        this.dialogService.open(
            ServiceTypeOperationUpsertDialogComponent,
            {
                header: translate.instant('serviceType.operation.label'),
                data: { operation: item },
                styleClass: 'p-dialog-width-60',
                focusOnShow: false,
                baseZIndex: 9010,
            },
        ).onClose.subscribe(() => {
            this.operationTable()?.reload();
        });
    }

    protected openRegionDetail(item: SocialServiceRegionObject | null = null): void {
        this.dialogService.open(
            ServiceTypeRegionUpsertDialogComponent,
            {
                header: translate.instant('serviceType.region.label'),
                data: { region: item },
                styleClass: 'p-dialog-width-60',
                focusOnShow: false,
            },
        ).onClose.subscribe((it) => {
            if (it) {
                this.regionTable()?.reload();
            }
        });
    }

    protected async deleteOperation(event: any): Promise<void> {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteSocialServiceOperation({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
                this.operationTable()?.reload();
            },
            event,
        );
    }

    protected async deleteRegion(event: any): Promise<void> {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteSocialServiceRegion({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
                this.regionTable()?.reload();
            },
            event,
        );
    }

    protected readonly getSocialServiceRegionsFrag = getSocialServiceRegionsFrag;
}
